import { AlertColor, SnackbarContentProps } from '@mui/material'
import { createSlice } from '@reduxjs/toolkit'
import { Action } from 'history'
import { type ParsedQuery } from 'query-string'

import { getRoute } from 'domains/application/Application'
import { UUID7 } from 'domains/organization/data-types'

export interface Account {
  name: string
  owner_id: UUID7
  id: UUID7
}

export interface Snack {
  id: string
  message: string
  duration?: number
  autoDismiss?: boolean
  color?: AlertColor
  props?: Omit<
    SnackbarContentProps,
    'variant' | 'sx' | 'ref' | 'defaultValue' | 'color'
  >
}

interface State {
  path: string
  urlArgs: ParsedQuery
  locationStack: [string, ParsedQuery][]
  previousLocation: string | undefined
  canGoBack: boolean
  pathArgs: Record<string, string>
  account?: Account
  windowHeight: number
  windowWidth: number
  flash: Record<string, string | undefined>
  snacks: Snack[]
  snackCount: number
  csrfToken?: string
}

const slice = createSlice({
  name: 'application',
  initialState: {
    path: '/',
    previousLocation: undefined,
    canGoBack: false,
    locationStack: [],
    urlArgs: {},
    pathArgs: {},
    windowHeight: 600,
    windowWidth: 1300,
    flash: {},
    snacks: [],
    snackCount: 0,
  } as State,
  reducers: {
    setPath: (
      state,
      action: {
        payload: {
          path: string
          clearFlash?: boolean
          urlArgs?: State['urlArgs']
          action: Action
        }
      },
    ) => {
      state.path = action.payload.path
      state.flash = action.payload.clearFlash === false ? state.flash : {}
      state.urlArgs = action.payload.urlArgs ?? {}
      const [_regexp, matcher, _element] = getRoute(state.path)
      const result = matcher(state.path)
      state.pathArgs = result === false ? {} : result.params
      state.canGoBack = true
    },
    setWindowSize(
      state,
      action: { payload: { width: number; height: number } },
    ) {
      state.windowHeight = action.payload.height
      state.windowWidth = action.payload.width
    },
    putFlash(state, action: { payload: { flash: Record<string, string> } }) {
      state.flash = action.payload.flash
    },
    putSnack(state, action: { payload: Omit<Snack, 'id'> }) {
      state.snacks.push({
        ...action.payload,
        id: `${state.snackCount + 1}`,
        autoDismiss: action.payload.autoDismiss ?? true,
      })
      state.snackCount += 1
    },
    popSnack(state, _action: { payload: undefined }) {
      state.snacks.shift()
    },
  },
})

export default slice
